import { FC, lazy, ReactNode, Suspense, useEffect } from 'react';
import { Navigate, Outlet, RouteObject } from 'react-router-dom';
import { ActivityIndicator } from '@kontentino/ui';
import routeConfig from 'app/config/routes';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';
import routes from 'constants/routes';
import OldApp from 'config/oldApp';
import { useIsMediaLibraryEnabled } from 'app/hooks/useIsMediaLibraryEnabled';

const MediaLibraryPage = lazy(
  () => import('app/modules/mediaLibraryPage/components/MediaLibraryPage'),
);

const MediaLibraryTrashPage = lazy(
  () =>
    import(
      'app/modules/mediaLibraryPage/components/trash/MediaLibraryTrashPage'
    ),
);

const PageLoader: FC = () => (
  <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-p-6">
    <ActivityIndicator />
  </div>
);

/**
 * Accounts outside the rollout keep master's library: the old app's page.
 * `replace` so Back does not land on this route and bounce forward again.
 */
export const MediaLibraryRolloutGate: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const isMediaLibraryEnabled = useIsMediaLibraryEnabled();

  useEffect(() => {
    if (!isMediaLibraryEnabled) {
      window.location.replace(OldApp.URL.MEDIA_LIBRARY);
    }
  }, [isMediaLibraryEnabled]);

  return isMediaLibraryEnabled ? <>{children}</> : <PageLoader />;
};

export const mediaLibraryPageRouter: RouteObject = {
  path: routeConfig.mediaLibrary.path,
  element: (
    <UserPermissionGate
      scopes={UserPermission.ACCESS_MEDIA_LIBRARY}
      RenderError={() => <Navigate to={routes.DASHBOARD} replace />}
    >
      <MediaLibraryRolloutGate>
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </MediaLibraryRolloutGate>
    </UserPermissionGate>
  ),
  children: [
    { index: true, element: <MediaLibraryPage /> },
    {
      path: routeConfig.mediaLibrary.routes.album.path,
      element: <MediaLibraryPage />,
    },
    {
      path: routeConfig.mediaLibrary.routes.trash.path,
      element: (
        <UserPermissionGate
          scopes={UserPermission.MEDIA_LIBRARY_MANAGE_MEDIA}
          RenderError={() => <Navigate to={routes.MEDIA_LIBRARY} replace />}
        >
          <MediaLibraryTrashPage />
        </UserPermissionGate>
      ),
    },
  ],
};
